import React from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import AuthNavigator from './AuthNavigator';
import MainTabNavigator from './MainTabNavigator';
import DiscoveryV2OnboardingScreen from '../screens/Onboarding/DiscoveryV2OnboardingScreen';
import IntroSlidesScreen from '../screens/Onboarding/IntroSlidesScreen';
import CalculationScreen from '../screens/Onboarding/CalculationScreen';
import NameInputScreen from '../screens/Onboarding/NameInputScreen';
import ProfileSummaryScreen from '../screens/Onboarding/ProfileSummaryScreen';
import TransferAuthScreen from '../screens/Auth/TransferAuthScreen';
import AbbreviatedOnboardingScreen from '../screens/Onboarding/AbbreviatedOnboardingScreen';
import DebugScreen from '../screens/Debug/DebugScreen';
import MentorGatewayDebugScreen from '../screens/Debug/MentorGatewayDebugScreen';
import TransferDebugScreen from '../screens/Debug/TransferDebugScreen';
import { RevenueCatDebugScreen } from '../screens/Debug/RevenueCatDebugScreen';
import TestRunnerScreen from '../screens/Debug/TestRunnerScreen';
import ManualTestsScreen from '../screens/Debug/ManualTestsScreen';
import { PaywallScreen } from '../screens/Paywall/PaywallScreen';
// import ProfileResultsScreen from '../screens/Onboarding/ProfileResultsScreen'; // UNUSED - kept for reference

import { QuestionAnswer, ProfileData } from '../services/discoveryOnboardingAPI';
import { auth } from '../config/firebase';

export type RootStackParamList = {
  Auth: undefined;
  DiscoveryV2Onboarding: undefined;
  IntroSlides: { mentor?: 'parenting' | 'leadership' | 'health' | 'default' } | undefined;
  CalculationScreen: {
    questionsAndAnswers: QuestionAnswer[];
  };
  NameInput: {
    profile: ProfileData;
  };
  ProfileSummary: {
    profile: ProfileData;
    userName: string;
  };
  // ProfileResults: { profile: ProfileData; }; // UNUSED - kept for reference
  TransferAuth: {
    token: string;
  };
  AbbreviatedOnboarding: {
    profile: any; // PrivateProfile
  };
  Debug: undefined;
  MentorGatewayDebug: undefined;
  TransferDebug: undefined;
  RevenueCatDebug: undefined;
  TestRunner: { autoTest?: boolean | string } | undefined;
  ManualTests: undefined;
  Paywall: undefined;
  Main: undefined;
};

const Stack = createNativeStackNavigator<RootStackParamList>();

interface RootNavigatorProps {
  isAuthenticated: boolean;
  hasCompletedOnboarding: boolean;
}

const RootNavigator: React.FC<RootNavigatorProps> = ({ isAuthenticated, hasCompletedOnboarding }) => {
  // Determine initial route based on auth and onboarding status
  const getInitialRouteName = (): keyof RootStackParamList => {
    // Only show Main if user has completed onboarding
    if (hasCompletedOnboarding) {
      return 'Main';
    }
    // If authenticated, check if user is anonymous
    if (isAuthenticated) {
      const currentUser = auth.currentUser;
      const isAnonymous = currentUser?.isAnonymous;
      
      // Anonymous users should see intro slides
      if (isAnonymous) {
        return 'IntroSlides';
      }
      
      // Non-anonymous authenticated users who haven't completed onboarding
      // go directly to discovery process (they already have an account from Google/Apple login)
      return 'DiscoveryV2Onboarding';
    }
    // Not authenticated - show intro slides (will sign in anonymously there)
    return 'IntroSlides';
  };

  // Force navigator to remount when auth/onboarding status changes so initialRouteName is reevaluated
  const navigatorKey = React.useMemo(
    () => `root-${isAuthenticated ? 'authed' : 'guest'}-${hasCompletedOnboarding ? 'onboarded' : 'needs-onboarding'}`,
    [isAuthenticated, hasCompletedOnboarding],
  );

  return (
    <Stack.Navigator 
      key={navigatorKey}
      initialRouteName={getInitialRouteName()}
      screenOptions={{ headerShown: false }}
    >
      <Stack.Screen name="Auth" component={AuthNavigator} />
      <Stack.Screen name="IntroSlides" component={IntroSlidesScreen} />
      <Stack.Screen name="DiscoveryV2Onboarding" component={DiscoveryV2OnboardingScreen} />
      <Stack.Screen name="CalculationScreen" component={CalculationScreen} />
      <Stack.Screen name="NameInput" component={NameInputScreen} />
      <Stack.Screen name="ProfileSummary" component={ProfileSummaryScreen} />
      {/* <Stack.Screen name="ProfileResults" component={ProfileResultsScreen} /> */}
      
      {/* External service transfer screens */}
      <Stack.Screen name="TransferAuth" component={TransferAuthScreen} />
      <Stack.Screen name="AbbreviatedOnboarding" component={AbbreviatedOnboardingScreen} />
      
      <Stack.Screen name="Debug" component={DebugScreen} />
      <Stack.Screen name="MentorGatewayDebug" component={MentorGatewayDebugScreen} />
      <Stack.Screen name="TransferDebug" component={TransferDebugScreen} />
      <Stack.Screen name="RevenueCatDebug" component={RevenueCatDebugScreen} />
      <Stack.Screen name="TestRunner" component={TestRunnerScreen} />
      <Stack.Screen name="ManualTests" component={ManualTestsScreen} />
      <Stack.Screen name="Paywall" component={PaywallScreen} />
      <Stack.Screen name="Main" component={MainTabNavigator} />
    </Stack.Navigator>
  );
  };

export default RootNavigator; 